import { useMemo } from 'react';
import {
    DataTable,
    visibleColumns,
    type DataTableMode,
    type DataTablePagination,
    type DataTableRefs,
    type DataTableSort
} from '@odo/shared-ui';
import type { BillingInvoiceListItem } from '../../../services/billing';
import { buildInvoiceColumns, type InvoicesSortBy } from './invoiceColumns';

export type { InvoicesSortBy };

interface InvoicesTableProps {
    invoices: BillingInvoiceListItem[];
    isLoading: boolean;
    sort: DataTableSort<InvoicesSortBy>;
    onOpenInvoice: (id: string) => void;
    pagination: DataTablePagination;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    onLoadMore: () => void;
    hasMore: boolean;
    // Viewport-fit measurement refs from useViewportPageSize, owned by
    // InvoicesSection and attached here to the elements DataTable renders.
    refs?: DataTableRefs;
}

export function InvoicesTable({
    invoices,
    isLoading,
    sort,
    onOpenInvoice,
    pagination,
    mode,
    onModeChange,
    onLoadMore,
    hasMore,
    refs
}: InvoicesTableProps) {
    const columns = useMemo(() => buildInvoiceColumns(), []);

    return (
        <DataTable
            label="Invoices"
            columns={columns}
            rows={invoices}
            getRowKey={(invoice) => invoice.id}
            rowAction={{ onActivate: (invoice) => onOpenInvoice(invoice.id), mode: 'click' }}
            sort={sort}
            pagination={pagination}
            mode={mode}
            onModeChange={onModeChange}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoading={isLoading}
            loadingRows={
                <tr>
                    <td colSpan={visibleColumns(columns).length} className="px-3 py-8 text-center">
                        <div className="flex items-center justify-center gap-2 text-gray-400">
                            <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                            <span className="text-sm">Loading invoices…</span>
                        </div>
                    </td>
                </tr>
            }
            emptyState="No invoices found."
            refs={refs}
        />
    );
}
